import * as React from "react";
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Badge,
} from "@repo/ui";
import { format } from "date-fns";
import { X, ChevronDown, Check } from "lucide-react";
import {
  InventorySearchCombobox,
  DateRangeFilter,
  type InventoryItem,
} from "@/components/forms";
import { cn } from "@repo/utils";
import { getWholesalers } from "@/lib/api/wholesalers";
import { getAuthUsers } from "@/lib/api/auth";

export interface ReceiptFilters {
  dateReceivedFrom?: string;
  dateReceivedTo?: string;
  wholesalerId?: string;
  userIds?: string;
  ndc?: string;
  itemName?: string;
  invoiceNumber?: string;
}

export interface ReceiptFiltersProps {
  onFilterChange: (filters: ReceiptFilters) => void;
  initialFilters?: ReceiptFilters;
}

interface Wholesaler {
  id: string;
  name: string;
}

interface User {
  id: string;
  initials: string;
  full_name: string | null;
  role: string;
}

export function ReceiptFilters({
  onFilterChange,
  initialFilters = {},
}: ReceiptFiltersProps) {
  const [wholesalers, setWholesalers] = React.useState<Wholesaler[]>([]);
  const [users, setUsers] = React.useState<User[]>([]);
  const [isLoadingWholesalers, setIsLoadingWholesalers] = React.useState(true);
  const [isLoadingUsers, setIsLoadingUsers] = React.useState(true);
  const [usersOpen, setUsersOpen] = React.useState(false);

  const [selectedWholesaler, setSelectedWholesaler] = React.useState(
    initialFilters.wholesalerId || "all"
  );
  const [selectedUserIds, setSelectedUserIds] = React.useState<string[]>(
    initialFilters.userIds ? initialFilters.userIds.split(",") : []
  );
  const [selectedInventoryItem, setSelectedInventoryItem] =
    React.useState<InventoryItem | null>(null);
  const [invoiceNumber, setInvoiceNumber] = React.useState(
    initialFilters.invoiceNumber || ""
  );
  const [dateFrom, setDateFrom] = React.useState<Date | undefined>(
    initialFilters.dateReceivedFrom
      ? new Date(initialFilters.dateReceivedFrom)
      : undefined
  );
  const [dateTo, setDateTo] = React.useState<Date | undefined>(
    initialFilters.dateReceivedTo
      ? new Date(initialFilters.dateReceivedTo)
      : undefined
  );
  const dateRange = { from: dateFrom, to: dateTo };

  React.useEffect(() => {
    const fetchWholesalers = async () => {
      try {
        const data = await getWholesalers();
        setWholesalers(data.data?.wholesalers || data.wholesalers || []);
      } catch (error) {
        console.error("Failed to fetch wholesalers:", error);
      } finally {
        setIsLoadingWholesalers(false);
      }
    };

    const fetchUsers = async () => {
      try {
        const data = await getAuthUsers({ pageSize: 100 });
        setUsers(data.data?.users || data.users || []);
      } catch (error) {
        console.error("Failed to fetch users:", error);
      } finally {
        setIsLoadingUsers(false);
      }
    };

    void fetchWholesalers();
    void fetchUsers();
  }, []);

  const buildFilters = React.useCallback(
    (
      overrides: Partial<{
        dateFrom: Date | undefined;
        dateTo: Date | undefined;
        wholesaler: string;
        userIds: string[];
        inventoryItem: InventoryItem | null;
        invoiceNumber: string;
      }> = {}
    ): ReceiptFilters => {
      const df = "dateFrom" in overrides ? overrides.dateFrom : dateFrom;
      const dt = "dateTo" in overrides ? overrides.dateTo : dateTo;
      const ws = overrides.wholesaler ?? selectedWholesaler;
      const uids = overrides.userIds ?? selectedUserIds;
      const item =
        "inventoryItem" in overrides
          ? overrides.inventoryItem
          : selectedInventoryItem;
      const inv = overrides.invoiceNumber ?? invoiceNumber;

      return {
        dateReceivedFrom: df ? format(df, "yyyy-MM-dd") : undefined,
        dateReceivedTo: dt ? format(dt, "yyyy-MM-dd") : undefined,
        wholesalerId: ws === "all" ? undefined : ws,
        userIds: uids.length > 0 ? uids.join(",") : undefined,
        ndc: item?.ndc || undefined,
        itemName: item?.itemName || undefined,
        invoiceNumber: inv || undefined,
      };
    },
    [
      dateFrom,
      dateTo,
      selectedWholesaler,
      selectedUserIds,
      selectedInventoryItem,
      invoiceNumber,
    ]
  );

  const activeFilterCount = [
    dateFrom,
    dateTo,
    selectedWholesaler !== "all",
    selectedUserIds.length > 0,
    selectedInventoryItem,
    invoiceNumber,
  ].filter(Boolean).length;

  const handleClearAll = () => {
    setDateFrom(undefined);
    setDateTo(undefined);
    setSelectedWholesaler("all");
    setSelectedUserIds([]);
    setSelectedInventoryItem(null);
    setInvoiceNumber("");
    onFilterChange({});
  };

  const toggleUser = (userId: string) => {
    const next = selectedUserIds.includes(userId)
      ? selectedUserIds.filter((id) => id !== userId)
      : [...selectedUserIds, userId];
    setSelectedUserIds(next);
    onFilterChange(buildFilters({ userIds: next }));
  };

  // Debounced invoice number filter — skip on mount, only fire when user changes the value
  const invoiceMountedRef = React.useRef(false);
  React.useEffect(() => {
    if (!invoiceMountedRef.current) {
      invoiceMountedRef.current = true;
      return;
    }
    const timer = setTimeout(() => {
      onFilterChange(buildFilters({ invoiceNumber }));
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceNumber]);

  const userLabel =
    selectedUserIds.length === 0
      ? "Users"
      : selectedUserIds.length === 1
        ? (users.find((u) => u.id === selectedUserIds[0])?.full_name ??
          users.find((u) => u.id === selectedUserIds[0])?.initials ??
          "1 user")
        : `${selectedUserIds.length} users`;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Date range */}
      <DateRangeFilter
        value={dateRange}
        onChange={({ from, to }) => {
          setDateFrom(from);
          setDateTo(to);
          onFilterChange(buildFilters({ dateFrom: from, dateTo: to }));
        }}
      />

      <div className="h-5 w-px bg-border" />

      {/* Wholesaler */}
      <Select
        value={selectedWholesaler}
        onValueChange={(val) => {
          setSelectedWholesaler(val);
          onFilterChange(buildFilters({ wholesaler: val }));
        }}
        disabled={isLoadingWholesalers}
      >
        <SelectTrigger
          className={cn(
            "h-9 w-[160px] text-sm font-normal",
            selectedWholesaler !== "all" && "border-primary/50 text-primary"
          )}
        >
          <SelectValue placeholder="Wholesaler" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Wholesalers</SelectItem>
          {wholesalers.map((w) => (
            <SelectItem key={w.id} value={w.id}>
              {w.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Users popover */}
      <Popover open={usersOpen} onOpenChange={setUsersOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "h-9 gap-1.5 font-normal",
              selectedUserIds.length > 0 && "border-primary/50 text-primary"
            )}
            disabled={isLoadingUsers}
          >
            {userLabel}
            {selectedUserIds.length > 0 && (
              <Badge
                variant="secondary"
                className="h-4 px-1 text-[10px] leading-none"
              >
                {selectedUserIds.length}
              </Badge>
            )}
            <ChevronDown className="h-3.5 w-3.5 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search users..." />
            <CommandList>
              <CommandEmpty>No users found.</CommandEmpty>
              <CommandGroup>
                {users.map((user) => (
                  <CommandItem
                    key={user.id}
                    onSelect={() => toggleUser(user.id)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedUserIds.includes(user.id)
                          ? "opacity-100"
                          : "opacity-0"
                      )}
                    />
                    {user.full_name || user.initials}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Inventory item */}
      {selectedInventoryItem ? (
        <div className="flex h-9 items-center gap-1 rounded-md border border-input bg-background px-3 text-sm">
          <span className="max-w-[150px] truncate text-sm">
            {selectedInventoryItem.itemName}
          </span>
          <button
            type="button"
            onClick={() => {
              setSelectedInventoryItem(null);
              onFilterChange(buildFilters({ inventoryItem: null }));
            }}
            className="ml-1 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <InventorySearchCombobox
          onSelect={(item) => {
            setSelectedInventoryItem(item);
            onFilterChange(buildFilters({ inventoryItem: item }));
          }}
          placeholder="Product..."
          searchPlaceholder="Search product..."
          className="h-9 w-[180px] text-sm"
        />
      )}

      {/* Invoice number */}
      <Input
        placeholder="Invoice #"
        value={invoiceNumber}
        onChange={(e) => setInvoiceNumber(e.target.value)}
        className="h-9 w-[110px] font-mono text-sm"
      />

      {/* Clear all */}
      {activeFilterCount > 0 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClearAll}
          className="h-9 px-2 text-muted-foreground hover:text-foreground"
        >
          Clear all
          <X className="ml-1 h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}
